import {Component, EventEmitter, forwardRef, Input, OnInit, Optional, Output, Self, TemplateRef} from '@angular/core';
import { NG_VALUE_ACCESSOR, NgControl, Validators, FormBuilder, FormGroup, FormArray, FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import {
  getEntityType,
} from '@portal-workspace/grow-ui-library';
import {setupUntilDestroy, getAddress2ComponentValueFormControlValueFn} from '@portal-workspace/grow-ui-library';
import {
  BusinessNumberSearchValue,
  compareMatch,
  EntityTypeCompanyOption,
  EntityTypes,
  EntityTypeValue,
  fromIndividualFormValue,
  IndividualFormValue,
  KybIndividualTrusteeDialogData,
  KybManualPerson,
  KybPerson,
  KybPersonRoleSelection,
  KybResult,
  toIndividualFormValue,
  KybSignerEntityView,
  kybDisplayStatus,
} from '@portal-workspace/grow-shared-library';
import { PersonalAssetComponentEvent, PersonalAssetsComponent } from './personal-assets.component';
import { PersonalLiabilitiesComponentEvent, PersonalLiabilitiesComponent } from './personal-liabilities.component';
import {AbstractControlValueAccessor} from '../abstract-control-value-accessor';
import {
  IndividualOrEntity,
  PlacesSearchResultFn,
  PlacesDetailsFn
} from '@portal-workspace/grow-shared-library';
import {delay, distinctUntilChanged, tap} from 'rxjs/operators';
import {UntilDestroy} from '@ngneat/until-destroy';
import {Subject, Subscription} from 'rxjs';
import {MARK, Mark} from '@portal-workspace/grow-ui-library/mark';
import {EntityTrustee, IndividualTrustee, TrusteeValue} from '@portal-workspace/grow-shared-library';
import { BusinessNumberSearchFn, BusinessNumberSearchComponent } from '../business-number-search-component/business-number-search.component';
import { MatFormFieldModule } from '@angular/material/form-field';
import { EntityTypeComponent } from '../entity-type-component/entity-type.component';
import { MatButtonModule } from '@angular/material/button';
import { AcnComponent } from '../acn-component/acn.component';
import { NameComponent } from '../name-component/name.component';
import { MarkDirective } from '../../directives/mark-as-dirty.directive';
import { IndividualFormComponent } from '../individual-form-component/individual-form.component';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { JsonPipe, NgTemplateOutlet } from '@angular/common';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { AuthService } from 'apps/portal2/src/app/service/auth.service';
import { KybEntitySectionComponent } from '../kyb-key-people-component/kyb-entity-section.component';
import { KybStatusComponent } from '../kyb-key-people-component/kyb-status.component';
import { ApplicationDialogService } from '../application-dialog-component/application-dialog.service';
import { ManualKybPeopleComponent } from '../kyb-key-people-component/manual-kyb-people.component';

export interface TrusteeComponentEvent {
  valid: boolean;
  entries: (IndividualTrustee | EntityTrustee)[];
}

@UntilDestroy({arrayName: 'subscriptions'})
@Component({
    selector: 'trustee',
    templateUrl: './trustee.component.html',
    styleUrls: ['./trustee.component.scss'],
    providers: [
        { provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => TrusteeComponent), multi: true },
        { provide: MARK, useExisting: forwardRef(() => TrusteeComponent) },
    ],
    standalone: true,
    imports: [NgTemplateOutlet, MatCheckboxModule, MatButtonToggleModule, FormsModule, ReactiveFormsModule, MarkDirective, NameComponent, AcnComponent, IndividualFormComponent, MatButtonModule, PersonalAssetsComponent, PersonalLiabilitiesComponent, BusinessNumberSearchComponent, EntityTypeComponent, MatFormFieldModule, KybEntitySectionComponent, KybStatusComponent, ManualKybPeopleComponent]
})
export class TrusteeComponent extends AbstractControlValueAccessor<TrusteeValue> implements OnInit, Mark {

  markObservable: Subject<boolean> = new Subject<boolean>();

  getAddress2ComponentValueFormControlValueFn = getAddress2ComponentValueFormControlValueFn;

  formGroup: FormGroup;
  formArray: FormArray;
  
  placesSearchResultFn: PlacesSearchResultFn | null = null;
  placesDetailsFn: PlacesDetailsFn | null = null;

  subscription?: Subscription;
  subscriptions: Subscription[] = [];

  disableControls:boolean = true;
  allowAddingTrustee:boolean = false;
  entityTypeReadonly = true;

  @Input({required: true}) businessNumberSearchFn!: BusinessNumberSearchFn;
  @Input({required: false}) showAssetsAndLiabilities = true;
  @Input({required: false}) showAddressForm = true;
  @Input({required: false}) showIdentification = false;
  // KYB "Key People" screen only captures name + dob for individual trustees; the full
  // detail (address/guarantor/contact) is collected on a later page.
  @Input({required: false}) kybMode = false;
  // KYB only: candidate individuals shown in the per-entity-trustee "Select signers" table. Resolved
  // per row by the container, so each corporate trustee shows its OWN officers rather than the
  // parent trust's stakeholders. Returns null until the row has a usable ABN/ACN; the view carries
  // the run's display status so an in-flight check is not shown as one that returned nobody.
  @Input({required: false}) signerEntityFor: ((row: FormGroup) => KybSignerEntityView | null) | null = null;
  // KYB only: handed to the signers table; see kyb-entity-section.
  @Input({required: false}) guarantorLockedStakeholderIds: ReadonlySet<number> = new Set<number>();
  // A corporate trustee is a company, so its signers table follows the company selection rules
  // (e.g. shareholder >= 25% preselected) via the shared kyb-entity-section logic.
  readonly signerEntityType: EntityTypes = 'company';
  // KYB only: extra content rendered inside an entity trustee's card, receiving that trustee's row
  // as $implicit. Used by the container to nest the parties of a trustee that is itself a trust or
  // partnership under the row it belongs to.
  @Input({required: false}) nestedPartyTemplate: TemplateRef<{ $implicit: FormGroup }> | null = null;
  // Key People only. Additional Guarantors leaves this off so an empty completed table stays read-only.
  @Input({required: false}) allowManualEntry = false;
  @Output() events: EventEmitter<TrusteeComponentEvent>;
  // KYB only: bubble Guarantor/Signer tick changes up to the kyb-key-people container.
  @Output() personRoleChange: EventEmitter<KybPersonRoleSelection> = new EventEmitter<KybPersonRoleSelection>();
  @Output() personSelectedChange: EventEmitter<{ person: KybPerson; selected: boolean }> = new EventEmitter<{ person: KybPerson; selected: boolean }>();
  // KYB only: a corporate trustee's own KYB check finished inside its "Select signers" panel, which
  // hands back the whole refreshed read model for the container to apply.
  @Output() signerResolved: EventEmitter<KybResult> = new EventEmitter<KybResult>();
  // KYB only: Retry pressed while a corporate trustee's entity is not in the read model yet, so the
  // container is the only one that can re-read it.
  @Output() signerRetry: EventEmitter<void> = new EventEmitter<void>();
  @Output() manualKybEntry = new EventEmitter<number>();
  @Output() manualPeopleChange = new EventEmitter<{ commercialEntityId: number; people: KybManualPerson[] }>();
  @Output() manualValidityChange = new EventEmitter<{ commercialEntityId: number; valid: boolean }>();

  // Entity trustees whose "Select signers" table is currently expanded, tracked per form group.
  private signersVisible: Set<FormGroup> = new Set<FormGroup>();

  // A write builds one row per trustee it was given, and each row would otherwise announce a list
  // that is still being assembled. Consumers read an emitted list as the complete set of trustees,
  // so a half-built one reads as "the trustees not added yet were removed".
  private seeding = false;

  constructor(private formBuilder: FormBuilder, private authService: AuthService, private dialogService: ApplicationDialogService) {
    super();
    this.events = new EventEmitter<TrusteeComponentEvent>();
    this.formArray = formBuilder.array([]);
    this.formGroup = formBuilder.group({
      trustees: this.formArray,
    });
    this.placesSearchResultFn = this.authService.getGooglePlacesSearchResultFn;
    this.placesDetailsFn = this.authService.getGooglePlacesDetailsFn;
  }

  ngOnInit() {
    setupUntilDestroy(this);
    this.subscription = this.formArray.valueChanges.pipe(
      delay(10),
      distinctUntilChanged(compareMatch),
      tap(array => {
        if (!this.formArray.valid) {
          this.propagateChange(null);
        } else {
          const trustees = this.formArrayToTrusteeValue();
          this.propagateChange(trustees.length ? trustees : null);
        }
        this.events.emit({valid: this.formArray.valid, entries: this.formArrayToTrusteeValue()})
      })
    ).subscribe();
  }

  formArrayToTrusteeValue(): (IndividualTrustee | EntityTrustee)[] {
    return ((this.formArray.value ?? []).map((v: any, index: number) => {
      if (v.type === 'Individual') {
        if (this.kybMode || !v.individual) {
          // kyb individuals only capture flat name + dob controls
          return v;
        }
        const individual = fromIndividualFormValue(v.individual);
        return {
          kind: 'Trustee',
          type: 'Individual',
          ...(individual ?? {}),
          personalAssets: v.personalAssets ?? null,
          personalLiabilities: v.personalLiabilities ?? null,
        } as IndividualTrustee;
      }
      else { // Entity
        if (!v.manualEntry && !v.organisation?.name) { // most probably invalid
          return null;
        }
        const acn = v.manualEntry ? v.stepacn : v.organisation?.acn;
        const name = v.manualEntry ? v.businessName : v.organisation?.name;
        const abn = v.manualEntry ? '' : v.organisation?.abn;
        const e: EntityTrustee = {
          kind: 'Trustee',
          type: 'Entity',
          manualEntry: v.manualEntry,
          organisation: {
            acn,
            abn,
            name,
            displayName: name,
          },
          organisationType: v.organisationType,
          abn: abn,
          acn: acn,
        }
        return e;
      }
    }).filter((v: any) => !!v));
  }


  trusteeFormGroups(): FormGroup[] {
    return this.formArray.controls as FormGroup[];
  }

  toggleSigners(trusteeFormGroup: FormGroup): void {
    if (this.signersVisible.has(trusteeFormGroup)) {
      this.signersVisible.delete(trusteeFormGroup);
    } else {
      this.signersVisible.add(trusteeFormGroup);
    }
  }

  isSignersVisible(trusteeFormGroup: FormGroup): boolean {
    return this.signersVisible.has(trusteeFormGroup);
  }

  signerEntity(trusteeFormGroup: FormGroup): KybSignerEntityView | null {
    return this.signerEntityFor ? this.signerEntityFor(trusteeFormGroup) : null;
  }

  startManualKyb(view: KybSignerEntityView): void {
    this.manualKybEntry.emit(view.entity.commercialEntityId);
  }

  showManualWaitingMessage(view: KybSignerEntityView): boolean {
    return kybDisplayStatus(view.entity.status) !== 'resolved';
  }

  // Names the waiting panel from the row itself, for the window before the row has an entity whose
  // legal name could be read instead.
  signerBusinessName(trusteeFormGroup: FormGroup): string | null {
    return trusteeFormGroup.get('manualEntry')?.value
      ? trusteeFormGroup.get('businessName')?.value ?? null
      : trusteeFormGroup.get('organisation')?.value?.name ?? null;
  }

  // Neither a trust nor a partnership has directors or shareholders for FrankieOne to return, so
  // such a trustee gets no signers table; its signers come from the trustees/partners collected in
  // the nested section instead. The type is the one the row's ABN/ACN lookup returned.
  hasNestedParties(trusteeFormGroup: FormGroup): boolean {
    const type = (trusteeFormGroup.get('organisationType')?.value as EntityTypeValue | null)?.type;
    return type === 'trust' || type === 'partnership';
  }

  trusteeFormGroupFormControl(trusteeFormGroup: FormGroup, controlName: string) {
    return trusteeFormGroup.controls[controlName] as FormControl;
  }

  // A trust needs at least 1 trustee, so removal is only offered above that floor.
  canRemoveTrustee(): boolean {
    return this.formArray.length > 1;
  }

  onPersonalAssetEvent($event: PersonalAssetComponentEvent, trusteeFormGroup: FormGroup) {
    if ($event.entries && $event.entries.length) { // there are assets entries
      const formControl = this.trusteeFormGroupFormControl(trusteeFormGroup, 'personalAssets');
      formControl.setValidators([Validators.required]);
      formControl.updateValueAndValidity()
    } else {
      const formControl = this.trusteeFormGroupFormControl(trusteeFormGroup, 'personalAssets');
      formControl.clearValidators();
      formControl.updateValueAndValidity()
    }
  }

  onPersonalLiabilitiesEvent($event: PersonalLiabilitiesComponentEvent, trusteeFormGroup: FormGroup) {
    if ($event.entries && $event.entries.length) {
      const formControl = this.trusteeFormGroupFormControl(trusteeFormGroup, 'personalLiabilities');
      formControl.setValidators([Validators.required]);
      formControl.updateValueAndValidity()
    } else {
      const formControl = this.trusteeFormGroupFormControl(trusteeFormGroup, 'personalLiabilities');
      formControl.clearValidators();
      formControl.updateValueAndValidity()
    }
  }

  removeTrustee(trusteeFormGroup: FormGroup) {
    const index = this.formArray.controls.indexOf(trusteeFormGroup);
    if (index !== -1) {
      this.formArray.removeAt(index);
    }
    this.signersVisible.delete(trusteeFormGroup);
    this.emitEvent();

  }

  addTrustee(g?: Exclude<TrusteeValue, null>[number]) {
    const formControlKind = this.formBuilder.control('Trustee');
    const formControlIndividualOrEntity = this.formBuilder.control<IndividualOrEntity>('Entity', [Validators.required]);
    const formControlPersonalAssets = this.formBuilder.control(null);
    const formControlPersonalLiabilities = this.formBuilder.control(null);
    const formGroup = this.formBuilder.group({
      kind: formControlKind,
      type: formControlIndividualOrEntity,
      personalAssets: formControlPersonalAssets,
      personalLiabilities: formControlPersonalLiabilities,
    });
    if (g) {
      switch(g.type) {
        case 'Individual': {
          (formGroup.controls as any).type.setValue('Individual');
          this.switchToIndividualTrustee(formGroup, g);
          break;
        }
        case 'Entity': {
          (formGroup.controls as any).type.setValue('Entity');
          this.switchToEntityTrustee(formGroup, g);
          break;
        }
      }
    } else {
      this.switchToEntityTrustee(formGroup, g);
    }
    this.formArray.push(formGroup);

    this.subscriptions.push(formControlIndividualOrEntity.valueChanges.pipe(
      delay(0),
      tap((r: IndividualOrEntity|null) => {
        switch (r) {
          case 'Individual': {
            this.switchToIndividualTrustee(formGroup);
            if (this.kybMode) {
              this.openKybIndividualDialog(formGroup);
            }
            break;
          }
          case 'Entity': {
            this.switchToEntityTrustee(formGroup);
            break;
          }
        }
      })
    ).subscribe());

    this.emitEvent();
  }

  doWriteValue(v: TrusteeValue | null | undefined): void | TrusteeValue {
    this.seeding = true;
    try {
      if (v) {
        for (const o of v) {
          this.addTrustee(o);
        }
      } else {
        this.addTrustee();
      }
    } finally {
      this.seeding = false;
    }
    this.emitEvent();
    return undefined;
  }

  private emitEvent() {
    if (this.seeding) {
      return;
    }
    this.events.emit({valid: this.formArray.valid, entries: this.formArray.value});
  }


  private switchToIndividualTrustee(formGroup: FormGroup, trustee?: IndividualTrustee) {
    formGroup.removeControl('organisation');
    formGroup.removeControl('organisationType');
    formGroup.removeControl('manualEntry');
    formGroup.removeControl('stepacn');
    formGroup.removeControl('businessName');

    if (this.kybMode) {
      // Only name + dob are captured on the KYB Key People screen; the remaining detail
      // is collected on the dedicated party detail page later in the flow.
      const formControlFirstName = this.formBuilder.control(trustee ? trustee.firstName : null, [Validators.required]);
      const formControlLastName = this.formBuilder.control(trustee ? trustee.lastName : null, [Validators.required]);
      const formControlMiddleName = this.formBuilder.control(trustee ? trustee.middleName : null);
      const formControlDob = this.formBuilder.control(trustee ? trustee.dob : null, [Validators.required]);

      formGroup.removeControl('individual');
      formGroup.removeControl('personalAssets');
      formGroup.removeControl('personalLiabilities');

      formGroup.addControl('firstName', formControlFirstName);
      formGroup.addControl('lastName', formControlLastName);
      formGroup.addControl('middleName', formControlMiddleName);
      formGroup.addControl('dob', formControlDob);
    } else {
      const formControlIndividual = this.formBuilder.control<IndividualFormValue>(toIndividualFormValue(trustee), [Validators.required]);
      const formControlPersonalAssets = this.formBuilder.control(trustee ? trustee.personalAssets : null);
      const formControlPersonalLiabilities = this.formBuilder.control(trustee ? trustee.personalLiabilities : null);

      formGroup.removeControl('firstName');
      formGroup.removeControl('lastName');
      formGroup.removeControl('middleName');
      formGroup.removeControl('dob');

      formGroup.addControl('individual', formControlIndividual);
      formGroup.setControl('personalAssets', formControlPersonalAssets);
      formGroup.setControl('personalLiabilities', formControlPersonalLiabilities);
    }
    this.allowAddingTrustee=true;
  }

  // KYB only: popup to capture name + dob for an individual trustee, then show it in the KYB table.
  openKybIndividualDialog(trusteeFormGroup: FormGroup): void {
    const data: KybIndividualTrusteeDialogData = {
      firstName: trusteeFormGroup.get('firstName')?.value ?? null,
      middleName: trusteeFormGroup.get('middleName')?.value ?? null,
      lastName: trusteeFormGroup.get('lastName')?.value ?? null,
      dob: trusteeFormGroup.get('dob')?.value ?? null,
    };
    this.subscriptions.push(
      this.dialogService.openKybIndividualTrusteeDialog(data).afterClosed().pipe(
        tap(result => {
          if (result) {
            trusteeFormGroup.get('firstName')?.setValue(result.firstName);
            trusteeFormGroup.get('middleName')?.setValue(result.middleName);
            trusteeFormGroup.get('lastName')?.setValue(result.lastName);
            trusteeFormGroup.get('dob')?.setValue(result.dob);
            trusteeFormGroup.markAllAsTouched();
          }
        }),
      ).subscribe(),
    );
  }

  isKybIndividualComplete(trusteeFormGroup: FormGroup): boolean {
    return !!trusteeFormGroup.get('firstName')?.value
      && !!trusteeFormGroup.get('lastName')?.value
      && !!trusteeFormGroup.get('dob')?.value;
  }

  kybIndividualName(trusteeFormGroup: FormGroup): string {
    return [trusteeFormGroup.get('firstName')?.value, trusteeFormGroup.get('middleName')?.value, trusteeFormGroup.get('lastName')?.value]
      .map(v => (v ?? '').toString().trim())
      .filter(v => !!v)
      .join(' ');
  }



  private switchToEntityTrustee(formGroup: FormGroup, trustee?: EntityTrustee) {
    const manualEntry = trustee ? trustee.manualEntry : false;

    const formControlOrganisation = this.formBuilder.control(!manualEntry ? trustee?.organisation ?? null : null, manualEntry ? [] : [Validators.required]);
    const formControlOrganisationType = this.formBuilder.control(!manualEntry ? trustee?.organisationType ?? EntityTypeCompanyOption : EntityTypeCompanyOption, manualEntry ? [] : [Validators.required] ); // entity-type-selection

    const formControlBusinessName = this.formBuilder.control(manualEntry ? trustee?.organisation?.name ?? null : null, manualEntry ? [Validators.required] : []);
    const formControlStepAcn = this.formBuilder.control(manualEntry ? trustee?.acn ?? null : null, manualEntry ? [Validators.required] : []);

    const formControlStepAllowManual = this.formBuilder.control<boolean | null>(manualEntry);



    formGroup.removeControl('individual');
    formGroup.removeControl('firstName');
    formGroup.removeControl('lastName');
    formGroup.removeControl('middleName');
    formGroup.removeControl('dob');
    formGroup.removeControl('personalAssets');
    formGroup.removeControl('personalLiabilities');

    formGroup.addControl('organisation', formControlOrganisation);
    formGroup.addControl('organisationType', formControlOrganisationType);
    formGroup.addControl('manualEntry',formControlStepAllowManual);
    formGroup.addControl('stepacn',formControlStepAcn);
    formGroup.addControl('businessName',formControlBusinessName);
    this.allowAddingTrustee=true;


    this.subscriptions.push(formControlOrganisation.valueChanges.pipe(
      tap((r: BusinessNumberSearchValue) => {
        if (r) {
          if (r.result) {
            const entityType = getEntityType(r.result)
            formControlOrganisationType.setValue(entityType)
            if (entityType?.type == 'other') {
              this.entityTypeReadonly = false
            } else {
              this.entityTypeReadonly = true
            }
          } else {
            this.entityTypeReadonly = !!formControlOrganisationType.value
          }
        }
      })
    ).subscribe());

    this.subscriptions.push(formControlStepAllowManual.valueChanges.pipe(
      delay(0),
      tap((r: any) => {
        if (r) {
          formControlStepAcn.setValidators(Validators.required)
          formControlBusinessName.setValidators(Validators.required)
          formControlOrganisation.clearValidators();
          formControlOrganisation.setValue(null);
        }
        else{
          formControlStepAcn.clearValidators();
          formControlStepAcn.setValue(null);
          formControlBusinessName.clearValidators();
          formControlBusinessName.setValue(null);
          formControlOrganisation.setValidators(Validators.required)
          formControlOrganisation.updateValueAndValidity();
        }
        this.formArray.updateValueAndValidity();
      })
    ).subscribe());

    this.formArray.updateValueAndValidity();
  }
  mark() {
    this.formGroup.markAllAsTouched();
    this.markObservable.next(true);
  }
}

